import React, { useCallback, useEffect, useId, useRef } from 'react';
import cn from 'classnames';
import Floating, { Placement, IFloatingProps, IFloatingRef, IReferenceElementProps } from '@creditflowresearch/uilib/Floating';

import useMenuKeyboardNavigation from '../hooks/useMenuKeyboardNavigation';
import styles from './menu.module.scss';

export type MenuPlacement = Placement;

export interface IMenuRef extends IFloatingRef {}

/** What Menu hands to a render-prop `trigger`. */
export interface IMenuTriggerRenderProps {
  /**
   * Spread onto the element that opens the menu. Carries Floating's ref and
   * interaction handlers plus the menu's `aria-haspopup`/`aria-expanded`/
   * `aria-controls` wiring.
   */
  referenceProps: IReferenceElementProps['referenceProps'],
  /** Current open state. */
  open: boolean,
  /** Id of the menu panel, already set as the trigger's `aria-controls`. */
  menuId: string,
}

/**
 * Either a plain element — which Menu wraps in its own trigger `<div>` — or a
 * render prop, which takes full control of the trigger and gets no wrapper DOM.
 */
export type MenuTrigger = React.ReactElement | ((props: IMenuTriggerRenderProps) => React.ReactElement);

interface IProps {
  dataTest?: string,
  disabled?: boolean,
  open?: boolean,
  setOpen?: (open: boolean) => void,
  trigger: MenuTrigger,
  children: React.ReactNode,
  className?: string,
  triggerClassName?: string,
  contentClassName?: string,
  placement?: MenuPlacement,
  usePortal?: boolean,
  isFlipEnabled?: boolean,
  triggerEvent?: 'click' | 'hover',
  positionFixed?: boolean,
  offsetOptions?: IFloatingProps['positioning']['offsetOptions'],
  /**
   * Where focus lands when the menu opens. `'container'` (default) focuses the
   * panel itself; `'firstItem'` focuses the first enabled item.
   */
  initialFocus?: 'container' | 'firstItem',
  /**
   * Focus the trigger again after Escape closes the menu. Only has an effect
   * when the trigger element is itself focusable — i.e. with a render-prop
   * `trigger` that puts `referenceProps` on a real control.
   */
  returnFocusOnClose?: boolean,
  /** Close the menu when an enabled item is activated. Disabled items leave it open. */
  closeOnItemClick?: boolean,
}

const MENU_ITEM_SELECTOR = '[role="menuitem"]:not([disabled])';

const Menu = React.forwardRef<IMenuRef, IProps>(({
  dataTest,
  disabled = false,
  open,
  setOpen,
  trigger,
  children,
  className = '',
  triggerClassName = '',
  contentClassName = '',
  placement = 'bottom-start',
  usePortal = true,
  isFlipEnabled = true,
  triggerEvent = 'click',
  positionFixed = false,
  offsetOptions = { mainAxis: 5, crossAxis: 5 },
  initialFocus = 'container',
  returnFocusOnClose = false,
  closeOnItemClick = false,
}, ref) => {
  const menuId = useId();
  const menuRef = useRef<HTMLDivElement>(null);
  const referenceRef = useRef<HTMLElement | null>(null);
  const hasRenderPropTrigger = typeof trigger === 'function';

  useEffect(() => {
    if (!open) return undefined;

    const frameId = requestAnimationFrame(() => {
      const menu = menuRef.current;
      if (!menu) return;

      const firstItem = initialFocus === 'firstItem'
        ? menu.querySelector<HTMLElement>(MENU_ITEM_SELECTOR)
        : null;

      // Falls back to the panel when asked for an item the menu doesn't have
      (firstItem ?? menu).focus();
    });

    return () => cancelAnimationFrame(frameId);
  }, [open, initialFocus]);

  // Handle Escape: close menu, optionally handing focus back to the trigger
  const handleMenuEscape = useCallback((): void => {
    setOpen?.(false);
    if (returnFocusOnClose) {
      referenceRef.current?.focus();
    }
  }, [setOpen, returnFocusOnClose]);

  // Menu keyboard navigation using shared hook
  const { handleKeyDown } = useMenuKeyboardNavigation({
    menuRef,
    itemSelector: MENU_ITEM_SELECTOR,
    onEscape: handleMenuEscape,
  });

  // Arrow keys on the trigger open the menu; focus is then placed by the effect above
  const handleTriggerKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>): void => {
    if (open) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen?.(true);
    }
  }, [open, setOpen]);

  const handleContentClick = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
    if (!closeOnItemClick) return;

    if ((e.target as HTMLElement).closest(MENU_ITEM_SELECTOR)) {
      setOpen?.(false);
    }
  }, [closeOnItemClick, setOpen]);

  const floatingMenu = (
    <Floating
      ref={ ref }
      controls={ { open, setOpen, disabled } }
      positioning={ {
        placement,
        usePortal,
        isFlipEnabled,
        positionFixed,
        offsetOptions,
      } }
      interactions={ {
        click: {
          enabled: triggerEvent === 'click',
          preventDefault: true,
        },
        hover: {
          enabled: triggerEvent === 'hover',
          handleClose: true,
        },
        dismiss: {
          onOutsidePress: true,
          onAncestorScroll: true,
        },
      } }
      renderOnlyVisible
      reference={ ({ referenceProps }) => {
        const { ref: floatingReferenceRef, onKeyDown: floatingOnKeyDown, ...restReferenceProps } = referenceProps;

        const menuReferenceProps = {
          ...restReferenceProps,
          ref: (node: HTMLElement | null) => {
            referenceRef.current = node;
            if (typeof floatingReferenceRef === 'function') floatingReferenceRef(node);
          },
          // Merge our keydown handler with Floating's
          onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
            handleTriggerKeyDown(e);
            if (typeof floatingOnKeyDown === 'function') floatingOnKeyDown(e);
          },
          'aria-haspopup': 'menu' as const,
          'aria-expanded': Boolean(open),
          'aria-controls': menuId,
        };

        if (typeof trigger === 'function') {
          return trigger({ referenceProps: menuReferenceProps, open: Boolean(open), menuId });
        }

        return (
          <div
            className={ triggerClassName }
            style={ { cursor: 'pointer' } }
            { ...menuReferenceProps }
          >
            { trigger }
          </div>
        );
      } }
      floating={ ({ contentProps, placement: floatingPlacement }) => {
        const { ref: floatingRef, style: floatingStyle, ...restContentProps } = contentProps;

        return (
          <div
            { ...restContentProps }
            ref={ (node) => {
              menuRef.current = node;
              if (typeof floatingRef === 'function') floatingRef(node);
            } }
            role="menu"
            aria-orientation="vertical"
            tabIndex={ -1 }
            style={ { ...floatingStyle, outline: 'none' } }
            data-placement={ floatingPlacement }
            className={ cn(styles.dropdownContent, contentClassName) }
            data-test={ dataTest }
            id={ menuId }
            onClick={ handleContentClick }
            onKeyDown={ handleKeyDown }
          >
            { children }
          </div>
        );
      } }
    />
  );

  // A render-prop trigger owns its own markup, so Menu adds no wrapper around it
  if (hasRenderPropTrigger) {
    return floatingMenu;
  }

  return (
    <div className={ cn(className, { 'is-disabled': disabled }) } data-test={ `${ dataTest }Container` }>
      { floatingMenu }
    </div>
  );
});

export default Menu;
